/* Purchase Confirmation — keep open AND keep content, with optional auto-dismiss
   - Default: no auto-dismiss (explicit close only)
   - To enable auto-dismiss: set ENABLE_AUTO_CLOSE = true and set CLOSE_AFTER_MS
*/
(() => {
  const ROOT_SEL        = '[data-purchase-confirmation-popup]';
  const INNER_SEL       = '.purchase-confirmation-popup__inner';
  const CLOSE_BTN_SEL   = '.purchase-confirmation-popup__close-button';
  const ACTIVE_CLASS    = 'active';
  const BODY_TAB_CLASS  = 'user-is-tabbing';

  /* =========================
     CONFIG
     ========================= */
  // Master toggle: when false, popup stays open until explicit close.
  const ENABLE_AUTO_CLOSE = true;    // set to true to enable auto-close
  const CLOSE_AFTER_MS     = 7000;    // auto-close delay (ms), e.g. 7000 = 7s
  let closeTimer = null;              // holds the timer id

  /* =========================
     (A) Disable the theme's auto-close path at the source
     - Many themes only start the built-in timer/hover-close if the body
       is NOT in a "user-is-tabbing" state. We force that state on.
     ========================= */
  function forceTabMode() {
    document.body.classList.add(BODY_TAB_CLASS);

    // If any script removes the class, put it back.
    new MutationObserver(() => {
      if (!document.body.classList.contains(BODY_TAB_CLASS)) {
        document.body.classList.add(BODY_TAB_CLASS);
      }
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  /* =========================
     (B) Content guard — if inner content gets emptied, restore last snapshot
     ========================= */
  let lastHTML = '';
  let innerObs = null;

  function watchInner(inner, root) {
    if (innerObs) innerObs.disconnect();

    innerObs = new MutationObserver(() => {
      const isActive = root.classList.contains(ACTIVE_CLASS);
      const nowHTML = inner.innerHTML.trim();

      // Capture first real content as a snapshot
      if (isActive && nowHTML && nowHTML !== lastHTML) {
        lastHTML = nowHTML;
      }

      // If active but content is emptied by theme logic, restore it
      if (isActive && !nowHTML && lastHTML) {
        inner.innerHTML = lastHTML;
      }
    });

    innerObs.observe(inner, { childList: true, subtree: true });
  }

  /* =========================
     (C) Optional auto-close
     - Prefer clicking the theme's own close button so it can clean up state/ARIA
     ========================= */
  function clearAutoCloseTimer() {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  }

  function startAutoCloseTimer(root) {
    if (!ENABLE_AUTO_CLOSE || CLOSE_AFTER_MS <= 0) return;
    clearAutoCloseTimer();
    closeTimer = setTimeout(() => autoClose(root), CLOSE_AFTER_MS);
  }

  function autoClose(root) {
    if (!root.classList.contains(ACTIVE_CLASS)) return;

    const closeBtn = root.querySelector(CLOSE_BTN_SEL);
    if (closeBtn) {
      closeBtn.click(); // lets the theme handle class/ARIA teardown
    } else {
      // Fallback if no button is found
      root.classList.remove(ACTIVE_CLASS);
      document.body.classList.remove('confirmation-popup-open');
    }
    clearAutoCloseTimer();
  }

  /* =========================
     (D) Boot — watch for open/close and maintain a content snapshot
     ========================= */
  function boot() {
    forceTabMode();

    const root = document.querySelector(ROOT_SEL);
    if (!root) return;

    const inner = root.querySelector(INNER_SEL);
    if (inner) watchInner(inner, root);

    // Observe popup state changes
    const rootObs = new MutationObserver(() => {
      if (root.classList.contains(ACTIVE_CLASS)) {
        // Small delay to allow the theme to inject the content
        setTimeout(() => {
          const i = root.querySelector(INNER_SEL);
          if (i && i.innerHTML.trim()) lastHTML = i.innerHTML.trim();
        }, 150);

        // Start (or restart) the auto-close timer if enabled
        startAutoCloseTimer(root);
      } else {
        // Popup just closed — clear any pending timer
        clearAutoCloseTimer();
      }
    });
    rootObs.observe(root, { attributes: true, attributeFilter: ['class'] });

    // If it happens to be open on load, snapshot and (optionally) start timer
    if (root.classList.contains(ACTIVE_CLASS)) {
      const i = root.querySelector(INNER_SEL);
      if (i && i.innerHTML.trim()) lastHTML = i.innerHTML.trim();
      startAutoCloseTimer(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();